import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { FiArrowLeft, FiChevronDown, FiDownload, FiEdit, FiFileText, FiTrash2 } from 'react-icons/fi';
import axios from 'axios';
import { formatDateOnlyUTC } from '../utils/dateOnly';
import { useAuth } from '../contexts/AuthContext';

interface EstimateItem {
  id: string;
  item_id?: string;
  name: string;
  description?: string;
  qty: number;
  rate: number;
  amount: number;
  is_taxable?: boolean;
}

interface Estimate {
  id: string;
  number: string;
  customer_id: string;
  issue_date: string;
  expiry_date?: string;
  status: 'pending' | 'approved' | 'rejected';
  sub_total: number;
  tax_total: number;
  total_amount: number;
  terms?: string;
  notes?: string;
  converted_invoice_id?: string | null;
  converted_invoice_number?: string | null;
  converted_at?: string | null;
  customer?: {
    id: string;
    first_name?: string;
    last_name?: string;
    display_name?: string;
    company_name?: string;
    email?: string;
  };
  items?: EstimateItem[];
}

const parseDownloadFilename = (contentDisposition?: string, fallbackName?: string) => {
  if (!contentDisposition) return fallbackName;
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1] || fallbackName;
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const EstimateDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token, isCookieMode, API_BASE_URL, csrfToken } = useAuth();
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [statusWarningDialogOpen, setStatusWarningDialogOpen] = useState(false);
  const [pendingStatusValue, setPendingStatusValue] = useState<'pending' | 'approved' | 'rejected' | null>(null);

  const buildHeaders = () => {
    const headers: any = {};
    if (!isCookieMode && token) {
      headers.Authorization = `Bearer ${token}`;
    }
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
    return headers;
  };

  const fetchEstimate = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/estimates/${id}`, {
        headers: buildHeaders(),
        withCredentials: isCookieMode,
      });
      setEstimate(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to fetch estimate');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchEstimate();
    }
  }, [id]);

  const handleDelete = async () => {
    if (!estimate) return;
    if (!window.confirm('Are you sure you want to delete this estimate? This action cannot be undone.')) return;
    try {
      setProcessing(true);
      await axios.delete(`${API_BASE_URL}/estimates/${estimate.id}`, {
        headers: buildHeaders(),
        withCredentials: isCookieMode,
      });
      navigate('/estimates?refresh=true');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to delete estimate');
      setProcessing(false);
    }
  };

  const handleStatusChange = async (status: 'pending' | 'approved' | 'rejected') => {
    if (!estimate) return;
    if (status === 'approved' && !estimate.converted_invoice_id) {
      setPendingStatusValue(status);
      setStatusWarningDialogOpen(true);
      setAnchorEl(null);
      return;
    }
    try {
      setProcessing(true);
      await axios.put(`${API_BASE_URL}/estimates/${estimate.id}`, {
        status,
      }, {
        headers: buildHeaders(),
        withCredentials: isCookieMode,
      });
      setSuccessMessage(`Estimate marked ${status}.`);
      setAnchorEl(null);
      await fetchEstimate();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update estimate status');
      setProcessing(false);
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmStatusChange = async () => {
    if (!pendingStatusValue) return;
    setStatusWarningDialogOpen(false);
    const nextStatus = pendingStatusValue;
    setPendingStatusValue(null);
    if (nextStatus === 'approved' && estimate && !estimate.converted_invoice_id) {
      await handleConvert();
      return;
    }
    await handleStatusChange(nextStatus);
  };

  const handleConvert = async () => {
    if (!estimate) return;
    try {
      setProcessing(true);
      const response = await axios.post(`${API_BASE_URL}/estimates/${estimate.id}/convert-to-invoice`, {}, {
        headers: buildHeaders(),
        withCredentials: isCookieMode,
      });
      setConvertDialogOpen(false);
      const successMessage = 'Estimate converted successfully.';
      const invoiceId = response.data?.invoiceId;
      if (invoiceId) {
        navigate(`/invoices/${invoiceId}`, { state: { successMessage } });
        return;
      }
      setSuccessMessage(successMessage);
      await fetchEstimate();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to convert estimate');
      setProcessing(false);
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (!estimate) return;
    try {
      setDownloading(true);
      const response = await axios.get(`${API_BASE_URL}/estimates/${estimate.id}/pdf`, {
        headers: buildHeaders(),
        responseType: 'blob',
        withCredentials: isCookieMode,
      });
      const fileName = parseDownloadFilename(response.headers['content-disposition'], `estimate-${estimate.number}.pdf`) || `estimate-${estimate.number}.pdf`;
      downloadBlob(new Blob([response.data]), fileName);
    } catch {
      setError('Failed to download estimate PDF');
    } finally {
      setDownloading(false);
    }
  };

  const getCustomerDisplayName = () => {
    if (!estimate?.customer) return 'Unknown Customer';
    return estimate.customer.display_name
      || estimate.customer.company_name
      || `${estimate.customer.first_name || ''} ${estimate.customer.last_name || ''}`.trim()
      || 'Unknown Customer';
  };

  const status = String(estimate?.status || '').toLowerCase();
  const statusSx =
    status === 'approved' ? { bgcolor: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0' } :
    status === 'rejected' ? { bgcolor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' } :
    { bgcolor: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !estimate) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-8 lg:px-8">
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-8 lg:px-8">
        <Alert severity="warning">Estimate not found</Alert>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 lg:px-8">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<FiArrowLeft />}
            onClick={() => navigate(`/customers/${estimate.customer_id}`)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Back to Customer
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: 20, sm: 24 }, letterSpacing: '-0.02em', color: 'text.primary' }}>
            Estimate #{estimate.number}
          </Typography>
          <Chip label={estimate.status.toUpperCase()} size="small" variant="outlined" sx={{ borderRadius: '10px', fontWeight: 600, ...statusSx }} />
        </Box>

        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<FiDownload />}
            onClick={handleDownload}
            disabled={downloading}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {downloading ? 'Downloading...' : 'Download PDF'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<FiEdit />}
            onClick={() => navigate(`/estimates/${estimate.id}/edit`)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            endIcon={<FiChevronDown />}
            onClick={(event) => setAnchorEl(event.currentTarget)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, backgroundColor: '#4a274f' }}
          >
            Actions
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-800 dark:bg-neutral-900 lg:col-span-2">
          <h2 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">Estimate Details</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="text-sm text-gray-500">Customer</p>
              <p className="font-semibold text-gray-900 dark:text-white">{getCustomerDisplayName()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Issue Date</p>
              <p className="font-semibold text-gray-900 dark:text-white">{formatDateOnlyUTC(estimate.issue_date)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Expiry Date</p>
              <p className="font-semibold text-gray-900 dark:text-white">{estimate.expiry_date ? formatDateOnlyUTC(estimate.expiry_date) : '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Converted Invoice</p>
              {estimate.converted_invoice_id ? (
                <button
                  type="button"
                  onClick={() => navigate(`/invoices/${estimate.converted_invoice_id}`)}
                  className="font-semibold text-sedra hover:underline"
                >
                  {estimate.converted_invoice_number || 'View linked invoice'}
                </button>
              ) : (
                <p className="font-semibold text-gray-900 dark:text-white">Not yet converted</p>
              )}
            </div>
          </div>

          {(estimate.terms || estimate.notes) && (
            <div className="mt-6 space-y-4 border-t border-gray-200 pt-4 dark:border-neutral-800">
              {estimate.terms && (
                <div>
                  <p className="text-sm text-gray-500">Terms</p>
                  <p className="whitespace-pre-wrap text-gray-900 dark:text-white">{estimate.terms}</p>
                </div>
              )}
              {estimate.notes && (
                <div>
                  <p className="text-sm text-gray-500">Notes</p>
                  <p className="whitespace-pre-wrap text-gray-900 dark:text-white">{estimate.notes}</p>
                </div>
              )}
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
          <h2 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">Totals</h2>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Subtotal</span>
              <span className="font-semibold text-gray-900 dark:text-white">${Number(estimate.sub_total || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Tax</span>
              <span className="font-semibold text-gray-900 dark:text-white">${Number(estimate.tax_total || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t border-gray-200 pt-3 text-base font-bold dark:border-neutral-800">
              <span>Total Estimate</span>
              <span>${Number(estimate.total_amount || 0).toFixed(2)}</span>
            </div>
          </div>
        </section>
      </div>

      <section className="mt-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
        <h2 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">Estimate Items</h2>
        {!estimate.items?.length ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No estimate items found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-800">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Item</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Qty</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Rate</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-neutral-800">
                {estimate.items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{item.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">{item.description || '-'}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-200">{Number(item.qty || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-200">${Number(item.rate || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900 dark:text-white">${Number(item.amount || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => handleStatusChange('pending')}>Mark Pending</MenuItem>
        <MenuItem onClick={() => handleStatusChange('approved')}>Mark Approved</MenuItem>
        <MenuItem onClick={() => handleStatusChange('rejected')}>Mark Rejected</MenuItem>
        <MenuItem onClick={() => { setAnchorEl(null); setConvertDialogOpen(true); }} disabled={processing || estimate.status === 'rejected'}>
          Convert to Invoice
        </MenuItem>
        <MenuItem onClick={() => { setAnchorEl(null); handleDelete(); }} sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={convertDialogOpen} onClose={() => setConvertDialogOpen(false)}>
        <DialogTitle>Convert to Invoice</DialogTitle>
        <DialogContent>
          This will convert this estimate into a new invoice, are you sure?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConvert} variant="contained" disabled={processing}>
            {processing ? 'Converting...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={statusWarningDialogOpen} onClose={() => !processing && setStatusWarningDialogOpen(false)}>
        <DialogTitle>Warning</DialogTitle>
        <DialogContent>
          This will convert this estimate into a new invoice and mark it approved. Continue?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (processing) return;
              setStatusWarningDialogOpen(false);
              setPendingStatusValue(null);
            }}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirmStatusChange} variant="contained" disabled={processing}>
            {processing ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EstimateDetailPage;
